import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Switch,
  useToast,
  Text,
  Flex,
  Badge,
  HStack,
  Tooltip,
  useColorModeValue,
  FormHelperText,
  InputGroup,
  InputRightElement,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
  TableContainer,
  Tag,
  TagLabel,
  Icon,
} from '@chakra-ui/react'
import { FiEdit2, FiTrash2, FiPlus, FiInfo, FiClock, FiServer, FiCheck } from 'react-icons/fi'
import { useAuth } from '../context/AuthContext'

interface Schedule {
  id: string
  appName: string
  dynoType: string
  dynoCount: number
  cronExpression: string
  isActive: boolean
}

const Schedules = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [apps, setApps] = useState<Array<{ id: string; name: string }>>([])
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { apiKey } = useAuth()
  const toast = useToast()
  
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700')
  const tableBorderColor = useColorModeValue('gray.100', 'gray.700')

  // Fetch apps from Heroku
  const fetchApps = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('https://api.heroku.com/apps', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'application/vnd.heroku+json; version=3',
        },
      })
      if (!response.ok) throw new Error('Failed to fetch apps')
      const data = await response.json()
      setApps(data.map((app: any) => ({ id: app.id, name: app.name })))
      
      // In a real app, we would fetch schedules from the backend here
      // For now, we'll just simulate loading
      setTimeout(() => {
        setIsLoading(false)
      }, 500)
    } catch (error) {
      toast({
        title: 'Error fetching apps',
        description: 'Failed to load your Heroku apps',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      setIsLoading(false)
    }
  }

  // Handle schedule form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    // Validate form
    const errors: Record<string, string> = {}
    if (!formData.get('appName')) errors.appName = 'App name is required'
    if (!formData.get('dynoType')) errors.dynoType = 'Dyno type is required'
    if (!formData.get('dynoCount')) errors.dynoCount = 'Dyno count is required'
    if (!formData.get('cronExpression')) errors.cronExpression = 'Cron expression is required'
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    
    const scheduleData = {
      id: selectedSchedule?.id || crypto.randomUUID(),
      appName: formData.get('appName') as string,
      dynoType: formData.get('dynoType') as string,
      dynoCount: parseInt(formData.get('dynoCount') as string),
      cronExpression: formData.get('cronExpression') as string,
      isActive: formData.get('isActive') === 'on',
    }

    try {
      // Here we would typically save to our backend
      if (selectedSchedule) {
        setSchedules(schedules.map(s => 
          s.id === selectedSchedule.id ? scheduleData : s
        ))
      } else {
        setSchedules([...schedules, scheduleData])
      }
      onClose()
      toast({
        title: 'Success',
        description: `Schedule ${selectedSchedule ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      setFormErrors({})
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${selectedSchedule ? 'update' : 'create'} schedule`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleDelete = (id: string) => {
    setSchedules(schedules.filter(s => s.id !== id))
    toast({
      title: 'Schedule deleted',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  const handleEdit = (schedule: Schedule) => {
    setSelectedSchedule(schedule)
    onOpen()
  }

  const handleAddNew = () => {
    setSelectedSchedule(null)
    onOpen()
  }

  const handleCloseModal = () => {
    setSelectedSchedule(null)
    setFormErrors({})
    onClose()
  }

  // Format cron expression to be more readable
  const formatCronExpression = (cron: string) => {
    // This is a simplified formatter - in a real app you might want a more sophisticated one
    const parts = cron.split(' ')
    if (parts.length !== 5) return cron
    
    // Very basic translation
    if (parts[0] === '0' && parts[1] === '0' && parts[2] === '*') {
      return 'Daily at midnight'
    } else if (parts[0] === '0' && parts[1] === '*' && parts[2] === '*') {
      return 'Hourly'
    } else if (parts[0] === '0' && parts[1] === '0' && parts[2] === '0') {
      return 'At midnight'
    }
    
    return cron
  }

  useEffect(() => {
    fetchApps()
  }, [apiKey])

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Box>
          <Heading size="lg" mb={2} color={textColor}>Schedules</Heading>
          <Text color={mutedTextColor}>
            Create and manage automated dyno scaling schedules
          </Text>
        </Box>
        <Button 
          leftIcon={<FiPlus />} 
          onClick={handleAddNew}
          colorScheme="brand"
        >
          New Schedule
        </Button>
      </Flex>

      {isLoading ? (
        <Box 
          bg={bgColor} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          p={6}
          boxShadow="sm"
        >
          <Stack spacing={4}>
            <Skeleton height="40px" width="40%" />
            <Skeleton height="20px" width="60%" />
            <Divider my={2} />
            <Skeleton height="50px" />
            <Skeleton height="50px" />
            <Skeleton height="50px" />
          </Stack>
        </Box>
      ) : schedules.length === 0 ? (
        <Box 
          bg={bgColor} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          p={8}
          boxShadow="sm"
          textAlign="center"
        >
          <Icon as={FiClock} boxSize={12} color="gray.400" mb={4} />
          <Heading size="md" mb={2}>No schedules yet</Heading>
          <Text color={mutedTextColor} mb={6}>
            Create your first schedule to automate dyno scaling
          </Text>
          <Button 
            leftIcon={<FiPlus />} 
            onClick={handleAddNew}
            colorScheme="brand"
            size="md"
          >
            Create Schedule
          </Button>
        </Box>
      ) : (
        <Box 
          bg={bgColor} 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          overflow="hidden"
          boxShadow="sm"
        >
          <TableContainer>
            <Table variant="simple">
              <Thead bg={useColorModeValue('gray.50', 'gray.900')}>
                <Tr>
                  <Th borderColor={tableBorderColor}>App</Th>
                  <Th borderColor={tableBorderColor}>Dyno Type</Th>
                  <Th borderColor={tableBorderColor}>Count</Th>
                  <Th borderColor={tableBorderColor}>Schedule</Th>
                  <Th borderColor={tableBorderColor}>Status</Th>
                  <Th borderColor={tableBorderColor} isNumeric>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {schedules.map((schedule) => (
                  <Tr 
                    key={schedule.id}
                    _hover={{ bg: hoverBgColor }}
                    transition="background-color 0.2s"
                  >
                    <Td borderColor={tableBorderColor} fontWeight="medium">{schedule.appName}</Td>
                    <Td borderColor={tableBorderColor}>
                      <HStack>
                        <Icon as={FiServer} color={mutedTextColor} />
                        <Text>{schedule.dynoType}</Text>
                      </HStack>
                    </Td>
                    <Td borderColor={tableBorderColor}>
                      <Tag 
                        size="md" 
                        colorScheme={schedule.dynoCount > 0 ? "green" : "red"}
                        borderRadius="full"
                      >
                        <TagLabel>{schedule.dynoCount}</TagLabel>
                      </Tag>
                    </Td>
                    <Td borderColor={tableBorderColor}>
                      <Tooltip label={schedule.cronExpression}>
                        <HStack>
                          <Icon as={FiClock} color={mutedTextColor} />
                          <Text>{formatCronExpression(schedule.cronExpression)}</Text>
                        </HStack>
                      </Tooltip>
                    </Td>
                    <Td borderColor={tableBorderColor}>
                      <Badge 
                        colorScheme={schedule.isActive ? 'green' : 'gray'}
                        borderRadius="full"
                        px={2}
                        py={1}
                      >
                        {schedule.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td borderColor={tableBorderColor} isNumeric>
                      <HStack spacing={2} justifyContent="flex-end">
                        <Tooltip label="Edit schedule">
                          <IconButton
                            aria-label="Edit schedule"
                            icon={<FiEdit2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEdit(schedule)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete schedule">
                          <IconButton
                            aria-label="Delete schedule"
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(schedule.id)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedSchedule ? 'Edit Schedule' : 'Create New Schedule'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Stack spacing={4}>
                <FormControl isRequired isInvalid={!!formErrors.appName}>
                  <FormLabel>Application</FormLabel>
                  <Select 
                    name="appName" 
                    placeholder="Select application" 
                    defaultValue={selectedSchedule?.appName}
                  >
                    {apps.map((app) => (
                      <option key={app.id} value={app.name}>{app.name}</option>
                    ))}
                  </Select>
                  {formErrors.appName && (
                    <FormHelperText color="red.500">{formErrors.appName}</FormHelperText>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={!!formErrors.dynoType}>
                  <FormLabel>Dyno Type</FormLabel>
                  <Input 
                    name="dynoType" 
                    placeholder="e.g. web, worker" 
                    defaultValue={selectedSchedule?.dynoType}
                  />
                  {formErrors.dynoType && (
                    <FormHelperText color="red.500">{formErrors.dynoType}</FormHelperText>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={!!formErrors.dynoCount}>
                  <FormLabel>Dyno Count</FormLabel>
                  <Input 
                    name="dynoCount" 
                    type="number" 
                    min="0" 
                    defaultValue={selectedSchedule?.dynoCount || 0}
                  />
                  {formErrors.dynoCount && (
                    <FormHelperText color="red.500">{formErrors.dynoCount}</FormHelperText>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={!!formErrors.cronExpression}>
                  <FormLabel>Schedule (Cron Expression)</FormLabel>
                  <InputGroup>
                    <Input 
                      name="cronExpression" 
                      placeholder="e.g. 0 0 * * *" 
                      defaultValue={selectedSchedule?.cronExpression}
                    />
                    <InputRightElement>
                      <Tooltip label="Cron format: minute hour day month weekday">
                        <Box>
                          <FiInfo />
                        </Box>
                      </Tooltip>
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText>
                    Format: minute hour day month weekday (e.g. 0 0 * * * for daily at midnight)
                  </FormHelperText>
                  {formErrors.cronExpression && (
                    <FormHelperText color="red.500">{formErrors.cronExpression}</FormHelperText>
                  )}
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="isActive" mb="0">
                    Active
                  </FormLabel>
                  <Switch 
                    id="isActive" 
                    name="isActive" 
                    colorScheme="brand"
                    defaultChecked={selectedSchedule?.isActive ?? true}
                  />
                </FormControl>

                <Alert status="info" borderRadius="md" variant="left-accent">
                  <AlertIcon />
                  <AlertDescription>
                    Schedules will automatically scale your dynos at the specified times.
                  </AlertDescription>
                </Alert>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                colorScheme="brand" 
                leftIcon={<FiCheck />}
              >
                {selectedSchedule ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default Schedules 